import { HubFeature } from '@ats/api/cm';
import { LocDef, LocDefs, LocMap } from '@cm/core/localization';

const pathStart = 'Settings|Localization|';
const sharedProperties: Pick<LocDef, 'shared' | 'htmlAllowed' | 'feature' | 'portal'> = {
	shared: false,
	htmlAllowed: false,
	feature: HubFeature.Core,
	portal: 'SP',
};

export type SPSettingsLocalizationPageLocNames =
	| 'SPSettingsLocalizationPageTitle'
	| 'SPSettingsLocalizationLabelPath'
	| 'SPSettingsLocalizationLabelResourceName'
	| 'SPSettingsLocalizationLabelUiType'
	| 'SPSettingsLocalizationLabelShared'
	| 'SPSettingsLocalizationLabelAllowsHtml'
	| 'SPSettingsLocalizationLabelDefaultValue'
	| 'SPSettingsLocalizationLabelCurrentValue'
	| 'SPSettingsLocalizationLabelDescription'
	| 'SPSettingsLocalizationLabelIsLegacy'
	| 'SPSettingsLocalizationButtonEdit'
	| 'SPSettingsLocalizationLabelName'
	| 'SPSettingsLocalizationLabelType'
	| 'SPSettingsLocalizationLabelPortal'
	| 'SPSettingsLocalizationLabelAllowsHtml'
	| 'SPSettingsLocalizationTableCaption'
	| 'SPSettingsLocalizationFilterByPortal'
	| 'SPSettingsLocalizationFilterByType';

export const spSettingsLocalizationLocs: LocDefs<SPSettingsLocalizationPageLocNames> = {
	SPSettingsLocalizationPageTitle: {
		defaultValue: 'Localizations',
		description: 'Title for the localizations page',
		displayPath: `${pathStart}Title`,
		displayType: 'Title',
		legacyName: 'Core/Settings.Localization.Title.PageTitle',
		...sharedProperties,
	},
	SPSettingsLocalizationLabelName: {
		defaultValue: 'Name',
		description: 'Label for the name column',
		displayPath: `${pathStart}Label|Name`,
		displayType: 'Label',
		legacyName: 'Core/Settings.Localization.ColumnHeading.Name',
		...sharedProperties,
	},
	SPSettingsLocalizationLabelType: {
		defaultValue: 'Type',
		description: 'Label for the type column',
		displayPath: `${pathStart}Label|Type`,
		displayType: 'Label',
		legacyName: 'Core/Settings.Localization.ColumnHeading.Type',
		...sharedProperties,
	},
	SPSettingsLocalizationLabelPortal: {
		defaultValue: 'Portal',
		description: 'Label for the portal column',
		displayPath: `${pathStart}Label|Portal`,
		displayType: 'Label',
		legacyName: 'Core/Settings.Localization.ColumnHeading.Portal',
		...sharedProperties,
	},

	SPSettingsLocalizationLabelPath: {
		defaultValue: 'Path',
		description: 'Label for the path column',
		displayPath: `${pathStart}Label|Path`,
		displayType: 'Label',
		legacyName: 'Core/Settings.Localization.ColumnHeading.Path',
		...sharedProperties,
	},
	SPSettingsLocalizationLabelResourceName: {
		defaultValue: 'Resource Name',
		description: 'Label for the resource name column',
		displayPath: `${pathStart}Label|Resource Name`,
		displayType: 'Label',
		legacyName: 'Core/Settings.Localization.ColumnHeading.ResourceName',
		...sharedProperties,
	},
	SPSettingsLocalizationLabelUiType: {
		defaultValue: 'UI Type',
		description: 'Label for the UI type column',
		displayPath: `${pathStart}Label|UI Type`,
		displayType: 'Label',
		legacyName: 'Core/Settings.Localization.ColumnHeading.UiType',
		...sharedProperties,
	},
	SPSettingsLocalizationLabelShared: {
		defaultValue: 'Shared',
		description: 'Label for the shared column',
		displayPath: `${pathStart}Label|Shared`,
		displayType: 'Label',
		legacyName: 'Core/Settings.Localization.ColumnHeading.Shared',
		...sharedProperties,
	},
	SPSettingsLocalizationLabelAllowsHtml: {
		defaultValue: 'Allows HTML',
		description: 'Label for the allows HTML column',
		displayPath: `${pathStart}Label|Allows HTML`,
		displayType: 'Label',
		legacyName: 'Core/Settings.Localization.ColumnHeading.HtmlAllowed',
		...sharedProperties,
	},
	SPSettingsLocalizationLabelDefaultValue: {
		defaultValue: 'Default Value',
		description: 'Label for the default value column',
		displayPath: `${pathStart}Label|Default Value`,
		displayType: 'Label',
		legacyName: 'Core/Settings.Localization.ColumnHeading.DefaultValue',
		...sharedProperties,
	},
	SPSettingsLocalizationLabelCurrentValue: {
		defaultValue: 'Current Value',
		description: 'Label for the current value column',
		displayPath: `${pathStart}Label|Current Value`,
		displayType: 'Label',
		legacyName: 'Core/Settings.Localization.ColumnHeading.CurrentValue',
		...sharedProperties,
	},
	SPSettingsLocalizationLabelDescription: {
		defaultValue: 'Description',
		description: 'Label for the description column',
		displayPath: `${pathStart}Label|Description`,
		displayType: 'Label',
		legacyName: 'Core/Settings.Localization.ColumnHeading.Description',
		...sharedProperties,
	},
	SPSettingsLocalizationLabelIsLegacy: {
		defaultValue: 'Is Legacy',
		description: 'Label for the is legacy column',
		displayPath: `${pathStart}Label|Is Legacy`,
		displayType: 'Label',
		legacyName: 'Core/Settings.Localization.ColumnHeading.IsLegacy',
		...sharedProperties,
	},
	SPSettingsLocalizationButtonEdit: {
		defaultValue: '',
		description: 'Label for the edit button',
		displayPath: `${pathStart}Button|Edit`,
		displayType: 'Button',
		legacyName: 'Core/Settings.Localization.Button.EditItem',
		...sharedProperties,
	},
	SPSettingsLocalizationTableCaption: {
		defaultValue: 'Localization Table',
		description: 'Caption for the localization table',
		displayPath: `${pathStart}Table|Caption`,
		displayType: 'Content',
		legacyName: 'Core/Settings.Localization.Table.Caption',
		...sharedProperties,
	},
	SPSettingsLocalizationFilterByPortal: {
		defaultValue: 'Filter by Portal',
		description: 'Filter by portal',
		displayPath: `${pathStart}Content|Portal Filter`,
		displayType: 'Content',
		legacyName: 'Core/Settings.Localization.FieldLabel.FilterPortal',
		...sharedProperties,
	},
	SPSettingsLocalizationFilterByType: {
		defaultValue: 'Filter by Type',
		description: 'Filter by type',
		displayPath: `${pathStart}Content|Type Filter`,
		displayType: 'Content',
		legacyName: 'Core/Settings.Localization.FieldLabel.FilterType',
		...sharedProperties,
	},
};

export type SPSettingsLocalizationPageLocType = LocMap<SPSettingsLocalizationPageLocNames>;
